import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, LayoutIcon, SettingsIcon, SmartphoneIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { PageCTA } from '../components/PageCTA';
import { ProblemSolution } from '../components/ProblemSolution';
import { SinglePlanOffer } from '../components/ui/SinglePlanOffer';
import { TestimonialsSection, type Testimonial } from '../components/ui/TestimonialsSection';
const services = [{
  icon: LayoutIcon,
  number: '01',
  title: 'Página web profesional',
  description: 'Una presencia clara y alineada con tu negocio para explicar qué haces y por qué importa.'
}, {
  icon: SmartphoneIcon,
  number: '02',
  title: 'Carta digital inteligente',
  description: 'Una experiencia móvil para presentar tu oferta sin PDFs, fricción ni información desactualizada.'
}, {
  icon: SettingsIcon,
  number: '03',
  title: 'Gestión incluida',
  description: 'Una base preparada para acompañarte después de publicar, con soporte y actualizaciones.'
}];
const principles = [['01', 'Claridad', 'Mensajes, jerarquía y acciones que el usuario entiende sin esfuerzo.'], ['02', 'Rendimiento', 'Una experiencia ligera, legible y adaptada a cada pantalla.'], ['03', 'Continuidad', 'Una presencia preparada para seguir siendo útil cuando tu negocio evoluciona.']];
const references: Testimonial[] = [{
  name: 'Negocio local',
  role: 'Referencia ilustrativa',
  text: 'El valor está en dejar de tener información repartida y contar con un lugar que explica el negocio de forma ordenada.'
}, {
  name: 'Empresa de servicios',
  role: 'Referencia ilustrativa',
  text: 'Una presencia clara permite que una primera visita llegue a una conversación con mucho mejor contexto.'
}, {
  name: 'Proyecto de hostelería',
  role: 'Referencia ilustrativa',
  text: 'Una carta digital bien pensada reduce preguntas repetidas y hace más simple elegir desde el móvil.'
}, {
  name: 'Marca personal',
  role: 'Referencia ilustrativa',
  text: 'Cuando el mensaje y la imagen trabajan juntos, es más fácil transmitir criterio antes de la primera llamada.'
}];
export function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="bg-black" data-id="element-354" data-mp="28r3lw56ucijx">
      <Hero data-id="element-355" data-mp="28o7ii1c851o3" />
      <ProblemSolution data-id="element-356" data-mp="23r8u7zf8roxt" />

      <section className="section-shell border-t border-white/10 bg-[#0a0a0a]" aria-labelledby="system-title" data-id="element-357" data-mp="23s0dcarnju77">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-358" data-mp="2ihoenbzu2sux">
          <div className="grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-359" data-mp="2dp4zwbpetroj">
            <div data-id="element-360" data-mp="1pysogquyhct7">
              <p className="eyebrow" data-id="element-361" data-mp="1py9blok5xem1">El sistema</p>
              <h2 id="system-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-362" data-mp="1l1989lep6pym">
                Las piezas adecuadas. Sin complejidad innecesaria.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base" data-id="element-363" data-mp="5bhzkgwlfexi">
              Un sistema digital debe explicar, orientar y permanecer útil. Por
              eso conectamos web, carta y gestión bajo una misma lógica.
            </p>
          </div>
          <div className="grid border-l border-t border-white/10 md:grid-cols-3" data-id="element-364" data-mp="j9pu6i65q45i">
            {services.map((service, index) => {
            const Icon = service.icon;
            return <motion.article key={service.number} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.15
            }} transition={{
              duration: 0.28,
              delay: index * 0.05,
              ease: [0.23, 1, 0.32, 1]
            }} className="group flex min-h-[250px] flex-col border-r border-b border-white/10 bg-black p-7 sm:p-8" data-id="element-365" data-mp="a7yp9fjfy1cp" data-mpi={index}>
                  <div className="flex items-start justify-between" data-id="element-366" data-mp="30ghucfh5ao4o">
                    <Icon size={22} className="text-white" aria-hidden="true" data-id="element-367" data-mp="1zm53bbmjkab" />
                    <span className="meta-number" data-id="element-368" data-mp="3t89ty192ppmm">{service.number}</span>
                  </div>
                  <h3 className="mt-10 text-lg font-medium tracking-[-0.02em] text-white" data-id="element-369" data-mp="3o7dqtpwjq7i9">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-370" data-mp="3k8mu3jvzsrvo">
                    {service.description}
                  </p>
                  <Link to="/servicios" className="mt-auto inline-flex w-fit items-center gap-2 pt-8 text-sm text-neutral-300 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-371" data-mp="28r56btut42us">
                    Ver detalle <ArrowRightIcon size={15} aria-hidden="true" data-id="element-372" data-mp="x9shfrty75yh" />
                  </Link>
                </motion.article>;
          })}
          </div>
        </div>
      </section>

      <SinglePlanOffer data-id="element-373" data-mp="170n612uo2hn3" />

      <section className="section-shell border-t border-white/10 bg-black" aria-labelledby="principles-title" data-id="element-374" data-mp="16z2bhd8cqyp8">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-375" data-mp="1c0ro2xb83kff">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-376" data-mp="1g11ugb2xgcut">
            <div data-id="element-377" data-mp="1kyk6iac8r871">
              <p className="eyebrow" data-id="element-378" data-mp="1kz3d4uwfya5r">El criterio</p>
              <h2 id="principles-title" className="mt-5 text-3xl font-medium tracking-[-0.045em] text-white sm:text-4xl" data-id="element-379" data-mp="3hs55h1gqbtfw">
                Una experiencia pensada para facilitar decisiones.
              </h2>
            </div>
            <p className="max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base" data-id="element-380" data-mp="tyci1ga39hsq">
              No prometemos cifras sin contexto. Diseñamos los factores que
              ayudan a una presencia digital a ser más comprensible, confiable y
              accionable.
            </p>
          </div>
          <div className="mt-10 grid overflow-hidden border border-white/10 sm:grid-cols-3" data-id="element-381" data-mp="y0fua74l0qf2">
            {principles.map(([number, title, detail]) => <div key={number} className="border-b border-white/10 p-7 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" data-id="element-382" data-mp="17vhj11uy3qh4">
                <p className="meta-number" data-id="element-383" data-mp="1hlu2eqr0xdlo">{number}</p>
                <h3 className="mt-10 text-lg font-medium text-white" data-id="element-384" data-mp="1hog96o63bp2u">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300" data-id="element-385" data-mp="1qn0ln7l252im">
                  {detail}
                </p>
              </div>)}
          </div>
          <Link to="/resultados" className="link-arrow mt-8" data-id="element-386" data-mp="2a6hb7jkqox0p">
            Ver nuestro enfoque <ArrowRightIcon size={15} aria-hidden="true" data-id="element-387" data-mp="2f7546asj3nxk" />
          </Link>
        </div>
      </section>

      <TestimonialsSection testimonials={references} data-id="element-388" data-mp="20a4lsxw895br" />
      <PageCTA data-id="element-389" data-mp="258o7qyatmh44" />
    </div>;
}